import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { SelectOption } from '../Select';
import { Oked } from '@app/models';
import { OkedModal } from 'modals';
import { getOkedList, getParentOkedList } from '@app/api';
import { cn } from 'lib/utils';
import { ChevronDown } from 'lucide-react';

interface OkedSelectProps {
  parentNumber?: number;
  onChange: (value: SelectOption<Oked>) => void;
  className?: string;
}

function OkedSelect(props: OkedSelectProps) {
  const { parentNumber = null, onChange, className } = props;
  const [pending, setPending] = useState<boolean>(true);
  const [option, setOption] = useState<SelectOption<Oked> | null>(null);
  const [options, setOptions] = useState<SelectOption<Oked>[]>([]);

  const onChangeSelect = useCallback(
    (selectedValue: string) => {
      const selectedOption = options.find(
        (opt) => opt.value.id === selectedValue
      );
      setOption(selectedOption || null);
      onChange(selectedOption as SelectOption<Oked>);
    },
    [onChange, options]
  );

  const getData = useCallback(async () => {
    try {
      const okedList = !parentNumber
        ? await getParentOkedList()
        : await getOkedList({
            parentNumber,
          });

      setOptions(
        okedList.map((item) => ({
          label: item.nameRu,
          value: item,
        }))
      );
      setPending(false);
    } catch (e) {
      setPending(false);
    }
  }, [parentNumber]);

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, [parentNumber, getData]);

  if (!pending && options.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="space-y-2">
        <Label>Выберите раздел</Label>
        <Select value={option?.value.id || ''} onValueChange={onChangeSelect}>
          <SelectTrigger>
            <SelectValue placeholder="Выберите" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value.id} value={option.value.id}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {option && (
        <OkedSelect parentNumber={option.value.number} onChange={onChange} />
      )}
    </div>
  );
}

interface OkedSelectTriggerProps {
  label?: string;
  placeholder?: string;
  value?: string;
  error?: boolean;
  helperText?: string;
  onChange: (value: SelectOption<Oked>, name: string) => void;
  name?: string;
  onClickClear?: () => void;
  parentNumber?: number;
  className?: string;
}

export function OkedSelectTrigger(props: OkedSelectTriggerProps) {
  const {
    label,
    placeholder,
    value,
    error,
    helperText,
    onChange,
    name,
    onClickClear,
    parentNumber,
    className,
  } = props;
  const [okedModalVisible, setOkedModalVisible] = useState<boolean>(false);

  const onClickShowOkedModal = useCallback(() => {
    setOkedModalVisible(true);
  }, []);

  const onCloseOkedModal = useCallback(() => {
    setOkedModalVisible(false);
  }, []);

  const onChangeOked = useCallback(
    (option?: SelectOption<Oked>) => {
      if (onChange) {
        onChange(option as SelectOption<Oked>, name as string);
      }
    },
    [name, onChange]
  );

  const onClickClearOked = useCallback(() => {
    if (onClickClear) {
      onClickClear();
    }
  }, [onClickClear]);

  return (
    <>
      <div className={cn('space-y-2', className)}>
        {label && <Label>{label}</Label>}
        <div className="relative">
          <Input
            value={value || ''}
            placeholder={placeholder}
            onClick={onClickShowOkedModal}
            readOnly
            className={cn(
              'cursor-pointer pr-8',
              error && 'border-destructive focus-visible:ring-destructive'
            )}
          />
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
            aria-hidden
          />
          {onClickClear && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
              onClick={onClickClearOked}
            >
              ×
            </Button>
          )}
        </div>
        {helperText && (
          <p
            className={cn(
              'text-sm text-muted-foreground',
              error && 'text-destructive'
            )}
          >
            {helperText}
          </p>
        )}
      </div>
      <OkedModal
        visible={okedModalVisible}
        onChange={onChangeOked}
        onClose={onCloseOkedModal}
        parentNumber={parentNumber}
      />
    </>
  );
}

export default memo(OkedSelect);
